

import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  Paper,
  Grid,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ReceiptIcon from "@mui/icons-material/Receipt";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import PaymentIcon from "@mui/icons-material/Payment";
import CreditScoreIcon from "@mui/icons-material/CreditScore";
import RefreshIcon from "@mui/icons-material/Refresh";
import VerifiedIcon from "@mui/icons-material/Verified";
import PendingIcon from "@mui/icons-material/Pending";
import { useNavigate, useSearchParams } from "react-router-dom";
import SubCard from "ui-component/cards/SubCard";
import { getVerifyHostedPageUrl, getVerifyPurchaseWithTransactionId } from "api/hiredrateURLConstruct";
import AppConfig from "api/AppConfig";

const Success = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hostedPageId = searchParams.get("id");
  const state = searchParams.get("state");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [transactionDetails, setTransactionDetails] = useState(null);
  const [paymentVerification, setPaymentVerification] = useState(null);
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [verificationAttempts, setVerificationAttempts] = useState(0);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const configObject = AppConfig("APIConfig");

  useEffect(() => {
    if (state !== "succeeded") {
      setError("Payment was not successful");
      setLoading(false);
      return;
    }

    if (!hostedPageId) {
      setError("No hosted page ID found");
      setLoading(false);
      return;
    }

    fetchAndVerifyTransaction();
  }, [hostedPageId, state]);

  const fetchAndVerifyTransaction = async () => {
    try {
      const verifyResponse = await fetch(getVerifyHostedPageUrl(), {
        method: "POST",
        ...configObject,
        headers: {
          ...configObject?.headers,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          hosted_page_id: hostedPageId,
        }),
      });

      const verifyData = await verifyResponse.json();

      if (verifyData.status !== "success") {
        setError(verifyData.message || "Failed to fetch transaction details");
        setLoading(false);
        return;
      }

      // The data is already in the correct structure
      const responseData = verifyData.data;

      // Extract transaction info from the normalized structure
      const transactionInfo = {
        transaction_id: responseData?.transaction?.payment?.transaction_id,
        hosted_page_id: responseData?.transaction?.hosted_page_id,
        invoice_id: responseData?.transaction?.invoice_id,
        subscription_id: responseData?.transaction?.subscription_id,
        customer_email: responseData?.transaction?.customer_email,
        currency: responseData?.transaction?.currency,
        payment_status: responseData?.transaction?.payment_status,
        total_amount: responseData?.transaction?.invoice?.total,
        transaction_date: responseData?.transaction?.payment?.transaction_date,
        event_type: responseData?.event_type,
        event_subtype: responseData?.event_subtype,
        items: responseData?.resource?.items || [],
        summary: responseData?.resource?.summary || {}
      };

      setTransactionDetails(transactionInfo);

      // Verify payment
      const transactionId = transactionInfo.transaction_id;
      if (transactionId) {
        await verifyPayment(transactionId);
      } else {
        console.log("No transaction_id found");
        setVerificationStatus('failed');
        setLoading(false);
      }
    } catch (err) {
      setError("Network error: " + err.message);
      setLoading(false);
    }
  };

  const verifyPayment = async (transactionId, isRetry = false) => {
    if (!isRetry) {
      setVerifyingPayment(true);
      setVerificationStatus('pending');
    }

    try {
      const response = await fetch(
        getVerifyPurchaseWithTransactionId(transactionId),
        {
          method: "GET",
          ...configObject,
          headers: {
            ...configObject?.headers,
            "Content-Type": "application/json",
          },
        }
      );

      const result = await response.json();

      const paymentData = result?.sso_response?.sso_response?.data || [];

      if (paymentData.length > 0) {
        const payment = paymentData[0];
        setPaymentVerification(payment);
        setVerificationStatus('verified');
        console.log("✅ Payment verified:", payment);
      } else {
        setVerificationStatus('failed');
      }
    } catch (err) {
      console.error("Error verifying payment:", err);
      setVerificationStatus('failed');
    } finally {
      if (!isRetry) {
        setVerifyingPayment(false);
        setLoading(false);
      }
    }
  };

  const handleRetryVerification = async () => {
    const transactionId = transactionDetails?.transaction_id;
    if (!transactionId) return;

    setVerificationAttempts(prev => prev + 1);
    setVerificationStatus('pending');
    setVerifyingPayment(true);

    const delay = Math.min(1000 * Math.pow(2, verificationAttempts), 10000);
    setTimeout(async () => {
      await verifyPayment(transactionId, true);
      setVerifyingPayment(false);
    }, delay);
  };

  const formatCurrency = (amount, currency = "INR") => {
    if (!amount) return "N/A";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toString().length === 10
      ? new Date(timestamp * 1000)
      : new Date(timestamp);
    return date.toLocaleString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background:
            "linear-gradient(135deg, #eef2ff 0%, #f5f3ff 50%, #ecfeff 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress sx={{ color: "white" }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background:
            "linear-gradient(135deg, #eef2ff 0%, #f5f3ff 50%, #ecfeff 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
        }}
      >
        <Card sx={{ maxWidth: 420, width: "100%", borderRadius: 4, p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button
            variant="contained"
            fullWidth
            onClick={() => navigate("/dashboard")}
          >
            Go to Dashboard
          </Button>
        </Card>
      </Box>
    );
  }

  const data = transactionDetails;
  const isAddon = data?.event_type === "addon";
  const currency = data?.currency || "INR";
  const items = data?.items || [];

  return (
    <SubCard
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 4,
      }}
    >
      <Card
        elevation={24}
        sx={{
          maxWidth: 650,
          width: "100%",
          borderRadius: 5,
          overflow: "hidden",
          position: "relative",
        }}
      >
        <Box
          sx={{
            background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
            p: 4,
            textAlign: "center",
            color: "white",
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 70, mb: 2 }} />
          <Typography variant="h4" fontWeight={700}>
            Payment Successful! 🎉
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
            Your {isAddon ? "add-on purchase" : "subscription change"} has been completed successfully
          </Typography>
        </Box>

        <CardContent sx={{ p: 3 }}>
          {/* Payment Verification Section */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              bgcolor: verificationStatus === 'verified' ? "#ecfdf5" :
                verificationStatus === 'pending' ? "#fef3c7" : "#fef2f2",
              borderRadius: 3,
              border: `1px solid ${verificationStatus === 'verified' ? "#10b981" :
                  verificationStatus === 'pending' ? "#f59e0b" : "#ef4444"
                }`,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              {verificationStatus === 'verified' ? (
                <VerifiedIcon sx={{ color: "#10b981" }} />
              ) : verificationStatus === 'pending' ? (
                <PendingIcon sx={{ color: "#f59e0b" }} />
              ) : (
                <PaymentIcon sx={{ color: "#ef4444" }} />
              )}
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Payment Verification
                </Typography>
                <Typography variant="body2" fontWeight={500}>

                  {verificationStatus === 'verified' && !paymentVerification && (
                    <>✅ Payment verified successfully</>
                  )}
                  {verificationStatus === 'pending' && (
                    <>⏳ Verifying payment... This may take a few moments</>
                  )}
                  {verificationStatus === 'failed' && (
                    <>⚠️ Payment not yet verified. Please wait or retry.</>
                  )}
                </Typography>
              </Box>
              {verificationStatus === 'failed' && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleRetryVerification}
                  disabled={verifyingPayment}
                  sx={{
                    borderColor: "#ef4444",
                    color: "#ef4444",
                  }}
                >
                  {verifyingPayment ? <CircularProgress size={20} /> : "Verify Now"}
                </Button>
              )}
              {verificationStatus === 'pending' && verifyingPayment && (
                <CircularProgress size={24} />
              )}
            </Stack>
          </Paper>

          {/* Payment Details */}
          {verificationStatus === 'verified' && paymentVerification && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 3,
                bgcolor: "#ecfdf5",
                borderRadius: 3,
                border: "1px solid #86efac",
              }}
            >
              <Stack spacing={1.5}>
                {/* <Typography variant="subtitle2" fontWeight={600} color="success.main">
                  Credits Purchased
                </Typography>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">Credits</Typography>
                  <Typography variant="h5" fontWeight={700} color="success.main">
                    {paymentVerification.credits_purchased}
                  </Typography>
                </Stack> */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">Purchase Cost</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatCurrency(paymentVerification.purchase_cost / 100, currency)}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Chip
                    label={paymentVerification.purchase_tx_response?.toUpperCase()}
                    size="small"
                    sx={{
                      bgcolor: paymentVerification.purchase_tx_response === "success" ? "#10b981" : "#f59e0b",
                      color: "white",
                    }}
                  />
                </Stack>
              </Stack>
            </Paper>
          )}

          {/* Transaction Info Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: "#fef2f2", borderRadius: 3 }}>
                <Typography variant="caption" color="error.main" fontWeight={600}>
                  Transaction ID
                </Typography>
                <Typography variant="body2" fontWeight={500} mt={1} fontFamily="monospace" fontSize="0.75rem">
                  {data?.transaction_id || "N/A"}
                </Typography>
              </Paper>
            </Grid>



            <Grid item xs={6}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: "#fffbeb", borderRadius: 3 }}>
                <Typography variant="caption" color="warning.main" fontWeight={600}>
                  Subscription ID
                </Typography>
                <Typography variant="body2" fontWeight={500} mt={1} fontFamily="monospace" fontSize="0.75rem">
                  {data?.subscription_id || "N/A"}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={6}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: "#f1f5f9", borderRadius: 3 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Invoice ID
                </Typography>
                <Typography variant="body2" fontWeight={500} mt={1} fontFamily="monospace" fontSize="0.75rem">
                  {data?.invoice_id || "N/A"}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Items List (if any) */}
          {items.length > 0 && (
            <>
              <Typography variant="subtitle2" fontWeight={600} mb={2} color="text.secondary">
                Items Purchased
              </Typography>
              <Stack spacing={2} sx={{ mb: 3 }}>
                {items.map((item, index) => (
                  <Paper key={index} elevation={0} sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{item.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Qty: {item.quantity} × {formatCurrency(item.unit_amount, currency)}
                        </Typography>
                      </Box>
                      <Typography variant="body2" fontWeight={700} color="primary.main">
                        {formatCurrency(item.amount, currency)}
                      </Typography>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </>
          )}

          {/* Financial Summary */}
          <Typography variant="subtitle2" fontWeight={600} mb={2} color="text.secondary">
            Payment Breakdown
          </Typography>
          <Paper elevation={0} sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: 3, mb: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={1} alignItems="center">
                <ReceiptIcon fontSize="small" color="action" />
                <Typography variant="body2">Total Amount</Typography>
              </Stack>
              <Typography variant="body2" fontWeight={600}>
                {formatCurrency(data?.total_amount, currency)}
              </Typography>
            </Stack>
            <Divider sx={{ my: 1.5 }} />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={1} alignItems="center">
                <CreditCardIcon fontSize="small" color="primary" />
                <Typography variant="body2" fontWeight={600}>Amount Charged</Typography>
              </Stack>
              <Typography variant="h6" fontWeight={700} color="primary.main">
                {formatCurrency(data?.total_amount, currency)}
              </Typography>
            </Stack>
          </Paper>

          {/* Transaction Details */}
          <Typography variant="subtitle2" fontWeight={600} mb={2} color="text.secondary">
            Transaction Details
          </Typography>
          <Paper elevation={0} sx={{ p: 2, bgcolor: "#f1f5f9", borderRadius: 3, mb: 3 }}>
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <PaymentIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">Transaction ID</Typography>
              </Stack>
              <Typography variant="body2" fontWeight={600} fontFamily="monospace">
                {data?.transaction_id || "N/A"}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1} mt={1}>
                <PaymentIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">Transaction Date</Typography>
              </Stack>
              <Typography variant="body2">{formatDate(data?.transaction_date)}</Typography>
            </Stack>
          </Paper>

          {/* Customer Info */}
          <Typography variant="subtitle2" fontWeight={600} mb={2} color="text.secondary">
            Customer Details
          </Typography>
          <Paper elevation={0} sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: 3, mb: 3 }}>
            <Typography variant="caption" color="text.secondary" display="block">Customer Email</Typography>
            <Typography variant="body2" fontWeight={500}>{data?.customer_email || "N/A"}</Typography>
          </Paper>

          {/* Action Button */}
          <Button
            variant="contained"
            fullWidth
            size="large"
            startIcon={<DashboardRoundedIcon />}
            onClick={() => navigate("/settings/subscription")}
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              py: 1.5,
              borderRadius: 3,
              textTransform: "none",
              fontSize: "1rem",
              fontWeight: 600,
            }}
          >
            close
          </Button>

          <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={2}>
            A confirmation email has been sent to your registered email address
          </Typography>
        </CardContent>
      </Card>
    </SubCard>
  );
};

export default Success;